import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

function IndicatorChartCard({ indicator, indicatorId, onClick }) {
  const { i18n } = useTranslation();
  
  const data = indicator.data;
  const latestValue = data.length > 0 ? data[data.length - 1].value : 0;
  
  // Calculate change from previous period (not from first value in entire dataset)
  // This prevents huge percentage when dataset includes very old data (e.g., 1970s)
  const previousValue = data.length > 1 ? data[data.length - 2].value : data.length > 0 ? data[0].value : 0;
  const totalChange = previousValue !== 0 ? ((latestValue - previousValue) / Math.abs(previousValue)) * 100 : 0;
  const isPositive = totalChange >= 0;
  
  // Format large numbers for better readability
  const formatValue = (num) => {
    if (num === null || num === undefined || isNaN(num)) return 'N/A';
    if (num === 0) return '0';
    
    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    
    // Billions (B)
    if (absNum >= 1e9) {
      return `${sign}${(absNum / 1e9).toFixed(2)}B`;
    }
    // Millions (M)
    if (absNum >= 1e6) {
      return `${sign}${(absNum / 1e6).toFixed(2)}M`;
    }
    // Thousands (K)
    if (absNum >= 1e3) {
      return `${sign}${(absNum / 1e3).toFixed(2)}K`;
    }
    // Small numbers - show appropriate decimals
    if (absNum >= 100) {
      return `${sign}${absNum.toFixed(1)}`;
    }
    if (absNum >= 10) {
      return `${sign}${absNum.toFixed(2)}`;
    }
    return `${sign}${absNum.toFixed(3)}`;
  };
  
  // Calculate min/max for better Y axis
  const values = data.map(d => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const padding = (maxValue - minValue) * 0.1 || 1;
  
  return (
    <Paper 
      elevation={2} 
      onClick={onClick}
      sx={{ 
        p: 2, 
        borderRadius: 2,
        borderTop: `3px solid ${isPositive ? '#4caf50' : '#f44336'}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        } : {},
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
            {indicatorId}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {i18n.language === 'vi' ? indicator.name_vi : indicator.name_en}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {formatValue(latestValue)}
          </Typography>
          <Chip
            icon={isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={`${isPositive ? '+' : ''}${totalChange.toFixed(1)}%`}
            size="small"
            color={isPositive ? 'success' : 'error'}
          />
        </Box>
      </Box>
      
      {/* Chart */}
      <Box sx={{ height: 180, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 10 }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
              }}
              interval="preserveStartEnd"
            />
            <YAxis 
              tick={{ fontSize: 10 }}
              domain={[minValue - padding, maxValue + padding]}
              tickFormatter={(value) => {
                const absValue = Math.abs(value);
                const sign = value < 0 ? '-' : '';
                if (absValue >= 1e9) return `${sign}${(absValue/1e9).toFixed(1)}B`;
                if (absValue >= 1e6) return `${sign}${(absValue/1e6).toFixed(1)}M`;
                if (absValue >= 1e3) return `${sign}${(absValue/1e3).toFixed(0)}K`;
                return value.toFixed(1);
              }}
            />
            <RechartsTooltip 
              labelFormatter={(value) => new Date(value).toLocaleDateString()}
              formatter={(value) => [formatValue(value), 'Value']}
              contentStyle={{ fontSize: 11, padding: '4px 8px' }}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={isPositive ? '#4caf50' : '#f44336'}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      
      {/* Footer Stats */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mt: 1, 
        pt: 1, 
        borderTop: '1px solid #f0f0f0',
        fontSize: '0.75rem',
        color: 'text.secondary'
      }}>
        <span>{data.length} points</span>
        <span>Min: {minValue.toFixed(1)} | Max: {maxValue.toFixed(1)}</span>
      </Box>
    </Paper>
  );
}

export default IndicatorChartCard;
